import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  signalingAllChatMessagesReceived,
  signalingChatMessageDeleted,
  signalingPublicChatCleared,
} from 'features/chat/actions';
import {
  CHAT_IMAGE_MAX_COUNT,
  PRIVATE_GROUP_CHAT_AVATAR_COLOR,
  PRIVATE_GROUP_CHAT_ID,
} from 'features/chat/constants';
import { processMessage } from 'features/chat/utils/processMessage';
import { setupUnreadCounters } from 'features/chat/utils/setupUnreadCounters';
import { senderKeyRatchetFinished } from 'features/e2ee/e2eeSlice';
import { signalingRoomJoined } from 'features/room/actions';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import {
  sidePanelClosed,
  sidePanelOpened,
  sidePanelPinned,
  sidePanelUnpinned,
  pinnedPanelsReceived,
} from 'features/side-panels/sidePanelsSlice';
import { UserId } from 'features/users/types';
import { selectLocalUser, selectUsers } from 'features/users/usersSlice';
import { RootState } from 'store/store';
import { resetMessages } from 'features/chat/utils/resetMessages';
import { dropStaleActiveView } from 'features/chat/utils/dropStaleActiveView';
import { findChatTargetUser } from 'features/chat/utils/findChatTargetUser';
import { getPrivateChatSenderId } from 'features/chat/utils/getPrivateChatSenderId';
import { deleteMessage } from 'features/chat/utils/deleteMessage';
import { collectOptimisticBlobUrls } from 'features/chat/utils/collectOptimisticBlobUrls';
import { selectPermissionsState } from 'features/permissions/permissionsSlice';
import { refinePermissions } from 'features/permissions/utils/refinePermissions';
import { PermissionTypes } from 'features/permissions/types';
import {
  ChatMessageId,
  ChatMessageImage,
  ChatImageDeletedPayload,
  ChatState,
  ChatTab,
  GroupedReaction,
  NewMessagePayload,
  PendingChatImage,
  PrivateChatOpenedPayload,
  PrivateChatView,
  ChatMessagePayload,
  SetMessageReactionsPayload,
  UnreadMessageCounterUpdatedPayload,
} from './types';

const clearPublicChats = (state: ChatState) => {
  state.publicIds = [];
  state.unreadCounters.public = 0;
  state.replyToId = null;
};

export const initialState: ChatState = {
  panelOpen: false,
  activeTab: ChatTab.public,
  localUserId: '',
  entities: {},
  publicIds: [],
  messageSentWithCurrentKey: false,
  senders: {},
  unreadCounters: {
    public: 0,
  },
  readMessages: {},
  privateChat: {
    chats: {},
    activeView: {
      name: 'list',
      data: {},
    },
    groupChat: {
      id: PRIVATE_GROUP_CHAT_ID,
      name: '',
      roles: [],
      avatarColor: PRIVATE_GROUP_CHAT_AVATAR_COLOR,
    },
  },
  exportProcessing: false,
  chatReactionsExtended: false,
  pendingImages: [],
  optimisticImageBlobs: {},
  imageLightbox: { open: false, messageId: null, index: 0 },
  replyToId: null,
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    activeTabChanged(state, action: PayloadAction<ChatTab>) {
      state.activeTab = action.payload;
      state.replyToId = null;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sendChatMessage(state, action: PayloadAction<NewMessagePayload>) {
      state.messageSentWithCurrentKey = true;
    },
    messageSent(state, action: PayloadAction<ChatMessagePayload>) {
      processMessage(state, action.payload);
      // Keep tracking the preview blobs while the confirmation carries no
      // images — the optimistic ones are still rendered and must be revocable
      // by the later cleanup paths.
      if (action.payload.images?.length && state.optimisticImageBlobs[action.payload.id]) {
        delete state.optimisticImageBlobs[action.payload.id];
      }
    },
    optimisticChatMessageAdded(state, action: PayloadAction<ChatMessagePayload>) {
      processMessage(state, action.payload);
      const blobs = collectOptimisticBlobUrls(action.payload.images);
      if (blobs.length > 0) {
        state.optimisticImageBlobs[action.payload.id] = blobs;
      }
    },
    optimisticChatMessageRemoved(state, action: PayloadAction<ChatMessageId>) {
      const id = action.payload;
      const message = state.entities[id];
      if (!message) {
        return;
      }
      deleteMessage(state, {
        id,
        userId: message.user.id,
        targetUserId: message.targetUser?.id,
        privateGroup: message.privateGroup,
      });
      if (state.optimisticImageBlobs[id]) {
        delete state.optimisticImageBlobs[id];
      }
    },
    privateChatOpened(state, action: PayloadAction<PrivateChatOpenedPayload>) {
      const { privateChat } = state;

      state.activeTab = ChatTab.private;

      if (!privateChat.chats[action.payload.id]) {
        privateChat.chats[action.payload.id] = {
          group: action.payload.group,
          lastActivity: new Date().toISOString(),
          messages: [],
        };
      }

      privateChat.activeView = {
        name: 'personal',
        data: {
          id: action.payload.id,
          group: action.payload.group,
        },
      };
      state.replyToId = null;
    },
    returnedToPrivateChatsView(state) {
      state.privateChat.activeView = {
        name: 'list',
        data: {},
      };
      state.replyToId = null;
    },
    chatMessagesReset(state) {
      resetMessages(state);
      state.privateChat.activeView = initialState.privateChat.activeView;
      state.activeTab = initialState.activeTab;
      state.replyToId = null;
    },
    unreadMessageCounterIncreased(
      state,
      action: PayloadAction<UnreadMessageCounterUpdatedPayload>
    ) {
      state.unreadCounters[action.payload.key] = state.unreadCounters[action.payload.key]
        ? state.unreadCounters[action.payload.key] + action.payload.modifier
        : action.payload.modifier;
    },
    messagesRead(
      state,
      action: PayloadAction<{
        messages: ChatMessageId[];
        chat?: string;
      }>
    ) {
      for (const messageId of action.payload.messages) {
        state.readMessages[messageId] = true;
      }

      if (action.payload.chat) {
        state.unreadCounters[action.payload.chat] = 0;
      }
    },
    publicChatCounterReset(state) {
      state.unreadCounters.public = 0;
    },
    groupChatCounterReset(state) {
      state.unreadCounters[state.privateChat.groupChat.id] = 0;
    },
    privateChatsCounterReset(state) {
      for (const key of Object.keys(state.unreadCounters)) {
        if (key !== 'public' && key !== state.privateChat.groupChat.id) {
          state.unreadCounters[key] = 0;
        }
      }
    },
    exportChatRequested: (state) => {
      state.exportProcessing = true;
    },
    exportChatFulfilled: (state) => {
      state.exportProcessing = false;
    },
    exportChatRejected: (state) => {
      state.exportProcessing = false;
    },
    publicChatCleared(state) {
      clearPublicChats(state);
    },
    messageReactionsSet(state, action: PayloadAction<SetMessageReactionsPayload>) {
      const { messageId, reactions } = action.payload;
      const message = state.entities[messageId];
      if (!message) {
        return;
      }
      message.reactions = reactions;
    },
    chatImagesAdded(state, action: PayloadAction<PendingChatImage[]>) {
      const remaining = Math.max(0, CHAT_IMAGE_MAX_COUNT - state.pendingImages.length);
      state.pendingImages.push(...action.payload.slice(0, remaining));
    },
    chatImageRemoved(state, action: PayloadAction<string>) {
      state.pendingImages = state.pendingImages.filter((img) => img.localId !== action.payload);
    },
    chatMessageImageRemoved(state, action: PayloadAction<ChatImageDeletedPayload>) {
      const { messageId, id } = action.payload;
      const message = state.entities[messageId];
      if (!message) {
        return;
      }
      message.images = message.images?.filter((img) => img.id !== id);
    },
    chatImagesClearPending(state) {
      state.pendingImages = [];
    },
    chatBlobUrlsCleared(state) {
      state.optimisticImageBlobs = {};
    },
    imageLightboxOpened(state, action: PayloadAction<{ messageId: ChatMessageId; index: number }>) {
      state.imageLightbox = {
        open: true,
        messageId: action.payload.messageId,
        index: action.payload.index,
      };
    },
    imageLightboxIndexChanged(state, action: PayloadAction<number>) {
      state.imageLightbox.index = action.payload;
    },
    imageLightboxClosed(state) {
      state.imageLightbox = { open: false, messageId: null, index: 0 };
    },
    replyStarted(state, action: PayloadAction<ChatMessageId>) {
      state.replyToId = action.payload;
    },
    replyCancelled(state) {
      state.replyToId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sidePanelOpened, (state, action) => {
        state.panelOpen = action.payload === 'chat';
      })
      .addCase(sidePanelClosed, (state) => {
        state.panelOpen = false;
      })
      .addCase(sidePanelPinned, (state, action) => {
        if (action.payload === 'chat') {
          state.panelOpen = true;
        }
      })
      .addCase(sidePanelUnpinned, (state, action) => {
        if (action.payload === 'chat') {
          state.panelOpen = false;
        }
      })
      .addCase(pinnedPanelsReceived, (state, action) => {
        if (action.payload.panels.includes('chat')) {
          state.panelOpen = true;
        }
      })
      .addCase(signalingRoomJoined, (state, action) => {
        state.localUserId = action.payload.user.id;
        state.localUserExternalId = action.payload.user.externalId;

        resetMessages(state);
        for (const message of action.payload.chatMessages) {
          processMessage(state, message);
        }

        setupUnreadCounters(state);
        dropStaleActiveView(state);
      })
      .addCase(signalingAllChatMessagesReceived, (state, action) => {
        const { messages } = action.payload;

        if (messages.length === 0) {
          resetMessages(state);
          state.privateChat.activeView = initialState.privateChat.activeView;
          state.activeTab = initialState.activeTab;
        } else {
          resetMessages(state);
          for (const message of action.payload.messages) {
            processMessage(state, message);
          }

          setupUnreadCounters(state);
          dropStaleActiveView(state);
        }
      })
      .addCase(signalingChatMessageDeleted, (state, action) => {
        deleteMessage(state, action.payload);
      })
      .addCase(signalingPublicChatCleared, (state) => {
        clearPublicChats(state);
      })
      .addCase(senderKeyRatchetFinished, (state, action) => {
        if (action.payload === 'application') {
          state.messageSentWithCurrentKey = false;
        }
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        Object.assign(state.privateChat.groupChat, {
          name: action.payload.settings.privateGroupChatName,
          roles: action.payload.settings.privateGroupChatRoles,
        });
        state.chatReactionsExtended = action.payload.settings.chatReactionsExtended;
      });
  },
});

export const {
  sendChatMessage,
  messageSent,
  optimisticChatMessageAdded,
  optimisticChatMessageRemoved,
  privateChatOpened,
  returnedToPrivateChatsView,
  chatMessagesReset,
  unreadMessageCounterIncreased,
  activeTabChanged,
  messagesRead,
  privateChatsCounterReset,
  publicChatCounterReset,
  groupChatCounterReset,
  exportChatRequested,
  exportChatRejected,
  exportChatFulfilled,
  publicChatCleared,
  messageReactionsSet,
  chatImagesAdded,
  chatImageRemoved,
  chatMessageImageRemoved,
  chatImagesClearPending,
  chatBlobUrlsCleared,
  imageLightboxOpened,
  imageLightboxIndexChanged,
  imageLightboxClosed,
  replyStarted,
  replyCancelled,
} = chatSlice.actions;

export const selectMessageEntities = (state: RootState) => state.chat.entities;
export const selectMessageEntity = createSelector(
  [selectMessageEntities, (state: RootState, id: ChatMessageId) => id],
  (byId, id) => byId[id]
);
export const selectPublicChatMessageIds = (state: RootState) => state.chat.publicIds;
export const selectLocalUserId = (state: RootState) => state.chat.localUserId;

const EMPTY_REACTIONS: GroupedReaction[] = [];
export const selectMessageReactions = (state: RootState, messageId: string): GroupedReaction[] =>
  state.chat.entities[messageId]?.reactions ?? EMPTY_REACTIONS;

export const selectIsMessageOptimistic = (state: RootState, id: ChatMessageId): boolean =>
  state.chat.entities[id]?.optimistic ?? false;

export const selectPublicChatMessages = createSelector(
  [selectMessageEntities, (state: RootState) => state.chat.publicIds],
  (byId, ids) => {
    const result = [];

    for (const id of ids) {
      result.push(byId[id]);
    }

    return result;
  }
);

export const selectPrivateChatEntities = (state: RootState) => state.chat.privateChat.chats;
export const selectActiveChatTab = (state: RootState) => state.chat.activeTab;
export const selectPrivateChatActiveView = (state: RootState) => state.chat.privateChat.activeView;
export const selectMessageSentWithCurrentKey = (state: RootState) =>
  state.chat.messageSentWithCurrentKey;

export const selectPrivateGroupChat = (state: RootState) => state.chat.privateChat.groupChat;

export const selectChatSenders = (state: RootState) => state.chat.senders;

export const selectChatPanelOpen = (state: RootState) => state.chat.panelOpen;

export const selectPrivateChats = createSelector(
  [
    selectUsers,
    selectChatSenders,
    selectPrivateChatEntities,
    selectLocalUser,
    selectPermissionsState,
  ],
  (userEntries, senders, chats, localUser, permissionsState) => {
    const result = [];

    for (const [targetChatId, chat] of Object.entries(chats)) {
      const activeUser = findChatTargetUser(userEntries, targetChatId);
      const targetUser = activeUser || senders[targetChatId];

      let canChatInPrivate = false;

      if (activeUser) {
        canChatInPrivate = refinePermissions(PermissionTypes.privateChat, {
          permissionsState,
          role: activeUser.role,
          localUser,
        });
      }

      if (chat.messages?.length && !chat.group) {
        result.push({
          id: getPrivateChatSenderId(targetUser),
          lastActivity: chat.lastActivity,
          name: targetUser.name!,
          avatarColor: targetUser.avatarColor,
          avatarUrl: targetUser.avatarUrl,
          initials: targetUser.initials,
          disabled: !activeUser || !canChatInPrivate,
        });
      }
    }

    // order by recent conversation
    result.sort((a, b) => new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime());

    return result;
  }
);

export const selectPrivateChat = createSelector(
  [
    selectMessageEntities,
    selectUsers,
    selectPrivateChatEntities,
    selectChatSenders,
    selectPrivateGroupChat,
    selectLocalUser,
    selectPermissionsState,
    (state: RootState, targetChatId: UserId) => targetChatId,
  ],
  (
    messageEntries,
    userEntries,
    chats,
    senders,
    groupChat,
    localUser,
    permissionsState,
    targetChatId
  ): PrivateChatView | null => {
    const chat = chats[targetChatId];
    if (!chat) {
      return null;
    }

    const messages = [];

    for (const id of chat.messages) {
      const message = messageEntries[id];
      messages.push(message);
    }

    if (chat.group) {
      return {
        id: groupChat.id,
        name: groupChat.name,
        group: chat.group,
        avatarColor: groupChat.avatarColor,
        messages,
        disabled: false,
      };
    }

    let canChatInPrivate = false;

    const activeUser = findChatTargetUser(userEntries, targetChatId);
    if (activeUser) {
      canChatInPrivate = refinePermissions(PermissionTypes.privateChat, {
        permissionsState,
        role: activeUser.role,
        localUser,
      });
    }

    const targetUser = activeUser || senders[targetChatId];
    if (!targetUser) {
      return null;
    }

    return {
      id: getPrivateChatSenderId(targetUser),
      name: targetUser.name!,
      avatarColor: targetUser.avatarColor!,
      avatarUrl: targetUser.avatarUrl,
      initials: targetUser.initials,
      targetId: targetUser.id,
      messages,
      disabled: !activeUser || !canChatInPrivate,
    };
  }
);

export const selectChatUnreadMessagesCount = (state: RootState) => {
  let total = 0;

  for (const n of Object.values(state.chat.unreadCounters)) {
    total += n;
  }

  return total;
};

export const selectChatUnreadMessagesCountByKey = (state: RootState, key: string) =>
  state.chat.unreadCounters[key] || 0;

export const selectChatExportProcessing = (state: RootState) => state.chat.exportProcessing;

export const selectChatReactionsExtended = (state: RootState) => state.chat.chatReactionsExtended;

export const selectPendingChatImages = (state: RootState) => state.chat.pendingImages;
export const selectOptimisticImageBlobs = (state: RootState, messageId: ChatMessageId) =>
  state.chat.optimisticImageBlobs[messageId];
export const selectAllOptimisticImageBlobs = (state: RootState) => state.chat.optimisticImageBlobs;
export const selectImageLightbox = (state: RootState) => state.chat.imageLightbox;

// Stable identity so useAppSelector does not re-render on every store change.
const NO_IMAGES: ChatMessageImage[] = [];

export const selectLightboxImages = (state: RootState) => {
  const { messageId } = state.chat.imageLightbox;

  return (messageId && state.chat.entities[messageId]?.images) || NO_IMAGES;
};

export const selectReplyToId = (state: RootState) => state.chat.replyToId;
export const selectReplyToMessage = createSelector(
  [selectMessageEntities, selectReplyToId],
  (byId, replyToId) => (replyToId ? byId[replyToId] : undefined)
);

export default chatSlice.reducer;
